if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        // Check if we're in quick-add context
        this.isQuickAdd = this.closest('.azantic-product-card-gallery__atc') !== null || this.closest('.azantic-product-card__atc') !== null;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Store the original margin-top value for the product form
        const productForm = document.querySelector('.leny-gallery-layout product-form');
        if (productForm) {
          this.originalMarginTop = window.getComputedStyle(productForm).marginTop;
        }

        // Check stock on load
        this.checkInitialStock();

        // Listen for variant changes
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        
        // Set up a mutation observer to detect when the variant-selects component is updated
        this.setupMutationObserver();
        if (!this.isQuickAdd) this.queueTimesactPreorderNormalize();
      }

      checkInitialStock() {
        const variantId = this.querySelector('[name="id"]')?.value;
        const inventoryData = document.querySelector(`[data-variant-inventory="${variantId}"]`);
        if (!inventoryData) return;

        const { quantity, inventory_management } = JSON.parse(inventoryData.textContent);
        // Use exactly the same logic as in onVariantChange
        const isLowStock = inventory_management === 'shopify' && 
          quantity <= 2 && 
          quantity > 0;
        this.updateStockWarnings(isLowStock);
      }

      /**
       * Updates the visibility of low stock warnings based on inventory status
       * @param {boolean} isLowStock - Whether the current variant has low stock
       */
      updateStockWarnings(isLowStock) {
        // Get both warnings
        const desktopWarning = document.querySelector('.low-stock-warning.desktop-only');
        const mobileWarning = document.getElementById('mobile-low-stock');
        if (!desktopWarning && !mobileWarning) return;

        // Update desktop warning
        if (desktopWarning) {
          desktopWarning.style.display = isLowStock ? 'block' : 'none';
        }

        // Update mobile warning
        if (mobileWarning) {
          mobileWarning.style.display = isLowStock ? 'inline-block' : 'none';
        }
      }

      onVariantChange({ data: { variant } }) {
        if (typeof variant.inventory_quantity === 'undefined') return;

        const isLowStock = variant.inventory_management === 'shopify' &&
          variant.inventory_quantity <= 2 &&
          variant.inventory_quantity > 0;

        // Force update of both desktop and mobile warnings
        this.updateStockWarnings(isLowStock);
        
        // Ensure mobile warning is also updated
        const mobileWarning = document.getElementById('mobile-low-stock');
        if (mobileWarning) {
          mobileWarning.style.display = isLowStock ? 'inline-block' : 'none';
        }

        // Adjust margin-top of product-form in leny-gallery-layout based on quantity
        const productForm = document.querySelector('.leny-gallery-layout product-form');
        if (productForm) {
          if (isLowStock) {
            productForm.style.marginTop = '14px';
          } else {
            // Restore the original margin-top if it exists
            if (this.originalMarginTop) {
              productForm.style.marginTop = this.originalMarginTop;
            }
          }
        }
      }

      /**
       * Sets up a mutation observer to watch for changes to the variant-selects component
       * This ensures the mobile LOW STOCK indicator updates correctly when variants change
       */
      setupMutationObserver() {
        // Create a mutation observer to watch for DOM changes
        this.observer = new MutationObserver((mutations) => {
          if (!this.isQuickAdd) this.queueTimesactPreorderNormalize();

          // Look for mutations that might have added a new mobile-low-stock element
          const mobileWarningMutation = mutations.find(mutation => {
            return Array.from(mutation.addedNodes).some(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                return node.querySelector && node.querySelector('#mobile-low-stock');
              }
              return false;
            });
          });

          // If we found a mutation that added a mobile-low-stock element
          if (mobileWarningMutation) {
            // Get the current variant ID
            const variantId = this.querySelector('[name="id"]')?.value;
            if (!variantId) return;

            // Get inventory data for this variant
            const inventoryData = document.querySelector(`[data-variant-inventory="${variantId}"]`);
            if (!inventoryData) return;

            // Parse the inventory data
            const { quantity, inventory_management } = JSON.parse(inventoryData.textContent);
            
            // Determine if this variant has low stock
            const isLowStock = inventory_management === 'shopify' && 
              quantity <= 2 && 
              quantity > 0;

            // Update the mobile warning
            const mobileWarning = document.getElementById('mobile-low-stock');
            if (mobileWarning) {
              mobileWarning.style.display = isLowStock ? 'inline-block' : 'none';
            }
          }
        });

        // Start observing the document body for DOM changes
        this.observer.observe(document.body, { 
          childList: true, 
          subtree: true 
        });
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        // Disconnect the observer when the component is removed
        if (this.observer) {
          this.observer.disconnect();
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const clickedButton = evt.submitter;

        // Only handle variant selection for quick add buttons
        if (this.isQuickAdd) {
          const variantId = clickedButton?.dataset.variantId;
          if (variantId) {
            this.variantIdInput.value = variantId;
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.isQuickAdd) {
          await this.prepareQuickAddPreorder(clickedButton);
        } else {
          await this.normalizeTimesactPreorder();
        }

        const formData = new FormData(this.form);

        // Always check for cart drawer first
        this.cart = document.querySelector('cart-drawer');

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        } else {
          // Fallback to cart notification if drawer not found
          this.cart = document.querySelector('cart-notification');
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }

            this.error = false;

            // Handle cart drawer or redirect
            if (this.cart) {
              if (this.cart.tagName.toLowerCase() === 'cart-drawer') {
                // Render contents first
                this.cart.renderContents(response);

                // Force open the drawer with a small delay to ensure rendering completes
                setTimeout(() => {
                  if (!this.cart.classList.contains('active')) {
                    this.cart.open();
                  }
                }, 200);
              } else {
                // For cart notification
                this.cart.renderContents(response);
              }
            } else {
              // Fallback to redirect if no cart components found
              window.location = window.routes.cart_url;
            }
          })
          .catch((e) => {
            // Fallback to redirect on error
            window.location = window.routes.cart_url;
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      async prepareQuickAddPreorder(clickedButton) {
        if (clickedButton?.dataset.timesactPreorder !== 'true') return;

        const productId = clickedButton?.dataset.productId || this.form.querySelector('[name="product-id"]')?.value;
        const variantId = clickedButton?.dataset.variantId || this.variantIdInput?.value;
        if (!productId || !variantId) return;

        this.removeTimesactInputs();

        const timesactProduct = await this.getTimesactProduct(productId);
        const timesactVariant = timesactProduct?.variants?.[variantId];
        const isTimesactPreorder =
          timesactVariant?.isPOEnabled &&
          (timesactVariant?.config?.status === 'ACTIVE' || timesactVariant?.config?.status === 'NO_STOCK');

        if (!isTimesactPreorder) return;

        const settings = await this.getTimesactSettings();
        const templateId = timesactVariant.config.settingsTemplate;
        const template = settings?.[templateId];
        const label = template?.cart?.labelText;
        const labelEnabled = typeof label?.isEnabled === 'undefined' || label.isEnabled;

        if (labelEnabled) {
          const key = this.populateTimesactText(label?.key || 'PRE ORDER', timesactVariant);
          const value = this.populateTimesactText(label?.value || 'THIS ITEM WILL SHIP ON {{shippingDate}}', timesactVariant);
          this.setPreorderProperty(key, value);
        }
      }

      async getTimesactProduct(productId) {
        const cachedProduct = this.getCachedTimesactProduct(productId);
        if (cachedProduct) return cachedProduct;

        try {
          const response = await fetch(`/apps/timesact/products?productIds=${productId}`);
          if (!response.ok) return null;
          const data = await response.json();
          const product = data?.products?.[productId] || null;
          if (product) this.cacheTimesactProduct(productId, product);
          return product;
        } catch (error) {
          return null;
        }
      }

      getCachedTimesactProduct(productId) {
        const products = window.ta?.products || this.readJsonStorage('products') || {};
        const product = products?.[productId];
        return product?.variants ? product : null;
      }

      cacheTimesactProduct(productId, product) {
        window.ta = window.ta || {};
        window.ta.products = window.ta.products || {};
        window.ta.products[productId] = product;
      }

      async getTimesactSettings() {
        const cachedSettings = window.ta?.settings || this.readJsonStorage('settings');
        if (cachedSettings) return cachedSettings;

        try {
          const response = await fetch('/apps/timesact/shop/settings');
          if (!response.ok) return null;
          const data = await response.json();
          if (data?.settings) {
            window.ta = window.ta || {};
            window.ta.settings = data.settings;
          }
          return data?.settings || null;
        } catch (error) {
          return null;
        }
      }

      readJsonStorage(key) {
        try {
          const value = localStorage.getItem(key);
          return value ? JSON.parse(value) : null;
        } catch (error) {
          return null;
        }
      }

      populateTimesactText(text, variant) {
        return text
          .replace('{{preorderQuantity}}', this.getTimesactQuantity(variant))
          .replace('{{shippingDate}}', this.getTimesactShippingDateForVariant(variant))
          .replace('{{daysLeftUntilShippingDate}}', this.getTimesactDaysLeftUntilShippingDate(variant?.config?.shippingDate))
          .replace('{{daysLeftUntilPreorderEndDate}}', this.getTimesactDaysLeftUntilEndDate(variant?.config));
      }

      getTimesactQuantity(variant) {
        const stock = variant?.config?.stock;
        if (!stock) return 'VARIABLE_NOT_SET';
        if (stock.option === 'STOCK3') return variant.quantity ?? 'VARIABLE_NOT_SET';
        if (stock.hasUnlimitedQuantity) return 'VARIABLE_NOT_SET';
        return stock.quantity ?? 'VARIABLE_NOT_SET';
      }

      getTimesactShippingDateForVariant(variant) {
        const shippingDate = this.getTimesactShippingDate(variant?.config?.shippingDate);
        if (shippingDate !== 'VARIABLE_NOT_SET') return shippingDate;

        const finalPaymentDate = variant?.config?.billingPolicy?.finalPayment?.date;
        if (finalPaymentDate) return this.formatTimesactDate(new Date(finalPaymentDate * 1000));

        return 'VARIABLE_NOT_SET';
      }

      getTimesactShippingDate(shippingDate) {
        if (!shippingDate || shippingDate.type === 'NO_SET') return 'VARIABLE_NOT_SET';

        if (shippingDate.type === 'DATE') {
          return this.formatTimesactDate(new Date(shippingDate.date * 1000));
        }

        if (shippingDate.type === 'PERIOD') {
          return this.formatTimesactDate(this.getTimesactPeriodDate(shippingDate.periodType, shippingDate.periodValue));
        }

        if (shippingDate.type === 'INTERVAL') {
          const start = this.getTimesactPeriodDate(shippingDate.periodStart?.type, shippingDate.periodStart?.value);
          const end = this.getTimesactPeriodDate(shippingDate.periodEnd?.type, shippingDate.periodEnd?.value);
          return `${this.formatTimesactDate(start)} - ${this.formatTimesactDate(end)}`;
        }

        return 'VARIABLE_NOT_SET';
      }

      getTimesactDaysLeftUntilShippingDate(shippingDate) {
        if (!shippingDate || shippingDate.type === 'NO_SET') return 'VARIABLE_NOT_SET';
        if (shippingDate.type === 'DATE') return this.getTimesactDaysBetween(new Date(), new Date(shippingDate.date * 1000));
        if (shippingDate.type === 'PERIOD') return this.getTimesactPeriodDays(shippingDate.periodType, shippingDate.periodValue);
        if (shippingDate.type === 'INTERVAL') return this.getTimesactPeriodDays(shippingDate.periodStart?.type, shippingDate.periodStart?.value);
        return 'VARIABLE_NOT_SET';
      }

      getTimesactDaysLeftUntilEndDate(config) {
        if (!config?.hasEndDate) return 'VARIABLE_NOT_SET';
        return this.getTimesactDaysBetween(new Date(), new Date(config.endDate * 1000));
      }

      getTimesactPeriodDate(type, value) {
        const date = new Date();
        date.setDate(date.getDate() + this.getTimesactPeriodDays(type, value));
        return date;
      }

      getTimesactPeriodDays(type, value) {
        const amount = parseInt(value, 10) || 0;
        if (type === 'WEEK') return amount * 7;
        if (type === 'MONTH') return amount * 30;
        return amount;
      }

      getTimesactDaysBetween(start, end) {
        return Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
      }

      formatTimesactDate(date) {
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
      }

      setPreorderProperty(key, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.id = 'preorder-label';
        input.name = `properties[${key}]`;
        input.value = value;
        this.form.appendChild(input);
      }

      removeTimesactInputs() {
        this.form.querySelectorAll('#preorder-label').forEach((input) => input.remove());
      }

      queueTimesactPreorderNormalize() {
        if (this.timesactNormalizeQueued) return;
        this.timesactNormalizeQueued = true;

        setTimeout(async () => {
          this.timesactNormalizeQueued = false;
          await this.normalizeTimesactPreorder();
        }, 100);
      }

      async normalizeTimesactPreorder() {
        const productId = this.form.querySelector('[name="product-id"]')?.value;
        const variantId = this.variantIdInput?.value;
        if (!productId || !variantId) return;

        const timesactProduct = await this.getTimesactProduct(productId);
        const timesactVariant = timesactProduct?.variants?.[variantId];
        if (!timesactVariant?.isPOEnabled) return;

        const fallbackDate = this.getTimesactShippingDateForVariant(timesactVariant);
        if (!fallbackDate || fallbackDate === 'VARIABLE_NOT_SET') return;

        this.form.querySelectorAll('#preorder-label').forEach((input) => {
          if (input.value.includes('VARIABLE_NOT_SET')) {
            input.value = input.value.replace(/VARIABLE_NOT_SET/g, fallbackDate);
          }
        });

        document.querySelectorAll('.timesact-preorder-description').forEach((message) => {
          if (message.textContent.includes('VARIABLE_NOT_SET')) {
            message.textContent = message.textContent.replace(/VARIABLE_NOT_SET/g, fallbackDate);
          }
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
